import { ReactElement, useCallback } from 'react';
import { BPMTableWidget } from '@Components/BPMTableWidget';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { TableLoading } from '@Components/TableEmptyState';
import {
  CONFIRMED_DNRS_HEADERS,
  DUPLICATES_HEADERS,
  getPotentiallyMissingHeaders,
  OVERSPEND_HEADERS,
  PRELOGS_ONLY_HEADERS,
  UNACCEPTED_HEADERS
} from './admin-constants';
import { LogSummariesWithConfirmedDNRs } from './admin-utils';

interface AdminTablesProps {
  logSummaries?: LogSummariesWithConfirmedDNRs;
  potentiallyMissingHeaders: ReturnType<typeof getPotentiallyMissingHeaders>;
  isLoading: boolean;
  error: unknown;
}

export const AdminTables = ({
  logSummaries,
  potentiallyMissingHeaders,
  isLoading,
  error
}: AdminTablesProps): ReactElement => (
  <div className='admin-page-tables'>
    <div className='admin-page-table-row'>
      <AdminTable
        title='Duplicate post-logs'
        dataTestId='duplicates-table'
        data={logSummaries?.duplicates}
        headers={DUPLICATES_HEADERS}
        isLoading={isLoading}
        error={error}
      />
      <AdminTable
        title='Unaccepted linear spots'
        dataTestId='unaccepted-table'
        data={logSummaries?.unaccepted}
        headers={UNACCEPTED_HEADERS}
        isLoading={isLoading}
        error={error}
      />
    </div>
    <div className='admin-page-table-row'>
      <AdminTable
        title='Possibly Missing Logs (order with no post-logs)'
        dataTestId='potentially-missing-table'
        data={logSummaries?.potentiallyMissing}
        headers={potentiallyMissingHeaders}
        isLoading={isLoading}
        error={error}
      />
      <AdminTable
        title='Overspend (post-log spend > ordered spend)'
        dataTestId='overspend-table'
        data={logSummaries?.overspend}
        headers={OVERSPEND_HEADERS}
        isLoading={isLoading}
        error={error}
      />
    </div>
    <div className='admin-page-table-row'>
      <AdminTable
        title='Confirmed DNRs Grouped by Week'
        dataTestId='confirmed-dnrs-table'
        data={logSummaries?.confirmedDNRs}
        headers={CONFIRMED_DNRS_HEADERS}
        isLoading={isLoading}
        error={error}
      />
    </div>
    <div className='admin-page-table-row'>
      <AdminTable
        title='BVS only for logs (no post-logs)'
        dataTestId='prelogs-only-table'
        data={logSummaries?.prelogsOnly}
        headers={PRELOGS_ONLY_HEADERS}
        isLoading={isLoading}
        error={error}
      />
    </div>
  </div>
);

interface AdminTableProps {
  title: string;
  dataTestId: string;
  data?: object[];
  headers: Header[];
  isLoading: boolean;
  error: unknown;
}

const AdminTable = ({ title, dataTestId, data, headers, isLoading, error }: AdminTableProps) => {
  const renderTableLoading = useCallback(
    () => <TableLoading isLoading={isLoading} error={error} />,
    [isLoading, error]
  );

  return (
    <BPMTableWidget header={title} dataTestId={dataTestId}>
      <BPMTable data={data ?? []} headers={headers} noRowsRenderer={renderTableLoading} />
    </BPMTableWidget>
  );
};
